'use client'

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '../components/Input';
import Button from '../components/Button';
import Spinner from "../components/Spinner";
import { useToaster } from '../context/ToasterContext';
import Breadcrumbs from '../components/Breadcrumbs';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSenders } from '../redux/globalDataSlice';
import api from '../api/axios';

const schema = yup.object().shape({
  name: yup.string().required('Sender name is required'),
  vendorAccountId: yup.string().required('Please select a connector'),
  active: yup.boolean(),
  number: yup.string().required('Phone number is required'),
  type: yup.string().required('Please select a type'),
  rate: yup
    .number()
    .typeError('Rate must be a number')
    .required('Rate is required'),
  notes: yup.string().optional(),
});

const typeOptions = [
  { value: 'TENDLC', label: 'TENDLC' },
  { value: 'TFN', label: 'TFN' },
  { value: 'SC', label: 'SHORT CODE' },
];

const pages = [{ name: 'Senders', href: '/senders' }];

export default function AddEditSender() {
  const { id } = useParams();
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { toaster } = useToaster();
  const dispatch = useDispatch();

  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);
  const { connectors, dataLoading } = useSelector((s) => s.globalData);

  const [loading, setLoading] = useState(true);
  const [senderData, setSenderData] = useState(null);
  const [connectorOptions, setConnectorOptions] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      // no hard-coded org/account defaults
      name: '',
      vendorAccountId: '',
      active: true,
      number: '',
      type: '',
      rate: '',
      notes: '',
    },
  });

  const connectorTypeValue = watch('vendorAccountId'); // just to keep Input in sync

  // Build connector options when connectors change
  useEffect(() => {
    if (Array.isArray(connectors)) {
      setConnectorOptions(
        connectors.map(c => ({ value: c.id, label: c.connectorName || c.id }))
      );
    }
  }, [connectors]);

  // For create mode, set hidden accountId once we have it
  useEffect(() => {
    if (!isEditMode && activeAccountId) {
      setValue('accountId', activeAccountId, { shouldDirty: false });
    }
  }, [isEditMode, activeAccountId, setValue]);

  // Fetch sender (edit mode only)
  const fetchSenderData = useCallback(async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/senders/account/${activeAccountId}/${id}`
      );
      setSenderData(response.data);
    } catch (error) {
      console.error('Fetch sender error:', error);
      toaster.error('Failed to load sender: ' + error);
    } finally {
      setLoading(false);
    }
  }, [id, toaster]);

  useEffect(() => {
    if (isEditMode) {
      fetchSenderData();
    } else {
      // create mode: wait for account if needed
      setLoading(false);
    }
  }, [isEditMode, fetchSenderData]);

  // Once sender + connectorOptions ready, fill the form (edit mode)
  useEffect(() => {
    if (isEditMode && senderData) {
      const itemExists = connectorOptions.some(opt => opt.value === senderData.vendorAccountId);
      reset({
        ...senderData,
        vendorAccountId: itemExists ? senderData.vendorAccountId : '',
        // carry accountId from data if present; otherwise fallback to active account
        accountId: senderData.accountId || activeAccountId || '',
      });
    }
  }, [isEditMode, senderData, connectorOptions, reset, activeAccountId]);

  const onSubmit = async (data) => {
    try {
      const accountId = data.accountId || activeAccountId;
      if (!accountId) {
        toaster.error('No account selected. Please choose an account first.');
        return;
      }

      const requestData = { ...data, accountId };

      if (isEditMode) {
        requestData.id = id;
        await api.put(
          `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/senders/account/${activeAccountId}/${id}`,
          requestData
        );
        toaster.success('Sender updated successfully!');
      } else {
        await api.post(
          `${process.env.REACT_APP_BASE_API_URL}/mvapi/utils/senders/account/${activeAccountId}`,
          requestData
        );
        toaster.success('Sender created successfully!');
      }

      // Refresh senders for the CURRENT account
      await dispatch(fetchSenders(accountId));

      navigate('/senders');
    } catch (error) {
      console.error('Error:', error);
      toaster.error('Something went wrong. Please try again.');
    }
  };

  const waitingForAccount = !isEditMode && !activeAccountId;

  return (
    <>
      {(loading || waitingForAccount || dataLoading) && <Spinner />}

      {!loading && !waitingForAccount && (
        <div className="px-4 sm:px-6 lg:px-8">
          <Breadcrumbs pages={pages} />

          <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
            {isEditMode ? 'Edit sender' : 'Create a sender'}
          </h2>
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {isEditMode ? 'Update the sender details below.' : 'Create or edit a sender here'}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-8 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <div className="space-y-12">
              <div className="border-b border-gray-200 dark:border-gray-800 pb-12">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
                  {/* Hidden field to carry accountId */}
                  <input
                    type="hidden"
                    {...register('accountId')}
                    value={isEditMode ? (senderData?.accountId || activeAccountId || '') : (activeAccountId || '')}
                    readOnly
                  />

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      label="Sender name"
                      placeholder="e.g. MyCompany"
                      required
                      {...register('name')}
                      error={errors.name?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      as="combobox"
                      required
                      label="Select a connector"
                      placeholder="Select an option"
                      options={connectorOptions}
                      value={connectorTypeValue}
                      onChange={(e) => {
                        setValue('vendorAccountId', e.target.value, { shouldValidate: true });
                      }}
                      error={errors.vendorAccountId?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      label="Phone number"
                      type="text"
                      maxLength="15"
                      required
                      placeholder="e.g. +16501234567"
                      {...register('number')}
                      error={errors.number?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1 sm:pt-5">
                    <Input
                      label="Active"
                      label_description="Check if you want to keep it as active"
                      as="checkbox"
                      {...register('active')}
                      error={errors.active?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      as="select"
                      required
                      label="Type"
                      options={typeOptions}
                      {...register('type')}
                      error={errors.type?.message}
                    />
                  </div>

                  <div className="col-span-2 sm:col-span-1">
                    <Input
                      type="number"
                      required
                      label="Rate limit (messages / sec)"
                      placeholder="e.g. 30"
                      {...register('rate')}
                      error={errors.rate?.message}
                    />
                  </div>

                  <div className="col-span-2">
                    <Input
                      as="textarea"
                      maxLength="500"
                      label="Notes"
                      placeholder="Optional notes..."
                      rows={4}
                      {...register('notes')}
                      error={errors.notes?.message}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-x-6">
            <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/senders')}
                  disabled={isSubmitting}
                >
                  Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting
                  ? (isEditMode ? 'Updating...' : 'Saving...')
                  : (isEditMode ? 'Update details' : 'Save details')}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
